import { useMemo, useState } from "react";
import {
  IdentifiableAnswerV3Input,
  LocalizedParticipantQuestion,
} from "../../../apis/API";
import { classifyQuestions } from "./classifyQuestions";

export interface QuestionFlow {
  /** The question to render now, or undefined when complete. */
  current: LocalizedParticipantQuestion | undefined;
  /** 0-based index among supported questions. */
  index: number;
  /**
   * Monotonic remount key, bumped by EVERY navigation mutator (`recordAnswer`,
   * `back`, `goTo`) — including a `goTo` to the current index. The host keys the
   * question component on this so entering a question always remounts it back to
   * its first screen, even when the index is unchanged (e.g. re-selecting the
   * current question from the menu — Figma's only back/change affordance). Owning
   * a single key here (rather than composing `index`+a nonce in the host) keeps
   * the "reset on entry" invariant in one place: any future nav path that goes
   * through these mutators is covered automatically.
   */
  instanceKey: number;
  /** 1-based position for display ("Question {step}/{total}"). */
  step: number;
  /** Count of supported (answerable) questions. */
  total: number;
  /** True once every supported question has been answered. */
  isComplete: boolean;
  /** The supported (answerable) questions, in display order — backs the menu. */
  questions: LocalizedParticipantQuestion[];
  /** All answers gathered so far — answered supported (in order) + skipped. */
  answers: IdentifiableAnswerV3Input[];
  /** The recorded answer for a question id, for resume / back re-display. */
  answerFor: (questionId: string) => IdentifiableAnswerV3Input | undefined;
  /** Record the current question's answer and advance. */
  recordAnswer: (answer: IdentifiableAnswerV3Input) => void;
  /** Step back to the previous question (no-op at the first). */
  back: () => void;
  /**
   * Jump to a supported question by index (Question List menu). Clamped to a
   * real question `[0, total-1]`; a no-op when there are no questions. Never
   * lands on the completed position (`total`) — the menu only navigates to
   * questions, so completion stays gated on sequential answering.
   *
   * Only bounds the index to a valid question; it does NOT enforce the
   * "no skipping ahead" rule. Callers that must prevent forward jumps gate which
   * indices they offer (the Question List menu disables not-yet-reached rows).
   */
  goTo: (index: number) => void;
  /** Whether the Question List menu should render section/group names. */
  displaySectionGroupNames: boolean;
}

/**
 * Flow engine for the question wizard. Classifies the assigned questions once,
 * sequences the supported ones, accumulates answers in `IdentifiableAnswerV3Input`
 * shape (seeded with the skipped records), and reports completion. State is
 * in-memory: it persists across re-renders/remounts while this hook stays
 * mounted, so the participant resumes where they left off within a session.
 */
export const useQuestionFlow = (
  questions: (LocalizedParticipantQuestion | null)[] | null | undefined,
  options?: { displaySectionGroupNames?: boolean | null },
): QuestionFlow => {
  const { supported, skipped } = useMemo(
    () => classifyQuestions(questions),
    [questions],
  );

  const [index, setIndex] = useState(0);
  // Bumped on every navigation so the host can force a question remount; see the
  // `instanceKey` doc on QuestionFlow.
  const [instanceKey, setInstanceKey] = useState(0);
  const bumpInstanceKey = () => setInstanceKey((k) => k + 1);
  const [given, setGiven] = useState<Record<string, IdentifiableAnswerV3Input>>(
    {},
  );

  const total = supported.length;
  const isComplete = index >= total;

  const recordAnswer = (answer: IdentifiableAnswerV3Input) => {
    setGiven((prev) => ({ ...prev, [answer.questionId]: answer }));
    setIndex((i) => Math.min(i + 1, total));
    bumpInstanceKey();
  };

  const back = () => {
    setIndex((i) => Math.max(0, i - 1));
    bumpInstanceKey();
  };

  const goTo = (target: number) => {
    if (total === 0) {
      return;
    }
    setIndex(Math.max(0, Math.min(target, total - 1)));
    // Bump even when the index is unchanged so re-selecting the current question
    // remounts it back to its first screen.
    bumpInstanceKey();
  };

  // Own-property guard so a question id colliding with an inherited
  // Object.prototype member (e.g. "toString", "constructor") can never return
  // the inherited function instead of a real/absent answer.
  const answerFor = (questionId: string): IdentifiableAnswerV3Input | undefined =>
    Object.prototype.hasOwnProperty.call(given, questionId)
      ? given[questionId]
      : undefined;

  const answers = useMemo(() => {
    const answered = supported
      .map((q) => answerFor(q.id ?? ""))
      .filter((a): a is IdentifiableAnswerV3Input => a != null);
    return [...answered, ...skipped];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supported, skipped, given]);

  return {
    current: supported[index],
    index,
    instanceKey,
    // Clamp to the supported count so the display position never exceeds total
    // once the flow is complete (index reaches `total`).
    step: Math.min(index + 1, total),
    total,
    isComplete,
    questions: supported,
    answers,
    answerFor,
    recordAnswer,
    back,
    goTo,
    displaySectionGroupNames: Boolean(options?.displaySectionGroupNames),
  };
};
